import React, { useState, useMemo } from "react";
import { Card } from "react-bootstrap";
import PropTypes from "prop-types";
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import "./PieChart.css";

// Color mapping for rankings
const COLORS = ['#075AD8', '#00BBD0', '#C3E500', '#ADB5BD'];

const PieChart = ({ title, type, data }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Process data based on chart type
  const processedData = useMemo(() => {
    if (!data) return [];

    let items = [];
    
    switch (type) {
      case 'region':
        // Use regions array + others
        items = data.regions?.map(region => ({
          name: region.location,
          value: region.percentage,
          displayValue: `$${region.total_income.toLocaleString()}`,
          displayUnit: '',
          displayName: region.location
        })) || [];
        
        if (data.others && data.others.percentage > 0) {
          items.push({
            name: 'Others',
            value: data.others.percentage,
            displayValue: `$${data.others.total_income.toLocaleString()}`,
            displayUnit: '',
            displayName: 'Others'
          });
        }
        break;

      case 'agent':
        // Use agents array + others, sorted by income_percentage
        items = data.agents?.map(agent => ({
          name: agent.agent?.name || 'Not Specified',
          value: agent.income_percentage,
          displayValue: `$${agent.total_income.toLocaleString()}`,
          displayUnit: '',
          displayName: agent.agent?.name || 'Not Specified'
        })) || [];
        
        if (data.others && data.others.income_percentage > 0) {
          items.push({
            name: 'Others',
            value: data.others.income_percentage,
            displayValue: `$${data.others.total_income.toLocaleString()}`,
            displayUnit: '',
            displayName: 'Others'
          });
        }
        break;

      case 'quote':
        // Use quote_ranges array
        items = data.quote_ranges?.map(range => ({
          name: range.range,
          value: range.percentage,
          displayValue: range.job_count,
          displayUnit: 'jobs',
          displayName: range.range
        })) || [];
        
        // Add others if exists in data
        if (data.others && data.others.percentage > 0) {
          items.push({
            name: 'Others',
            value: data.others.percentage,
            displayValue: data.others.job_count || 'Others',
            displayUnit: data.others.job_count ? 'jobs' : '',
            displayName: 'Others'
          });
        }
        break;

      case 'property':
        // Use property_types array
        items = data.property_types?.map(property => ({
          name: property.type,
          value: property.percentage,
          displayValue: property.job_count,
          displayUnit: 'jobs',
          displayName: property.type
        })) || [];
        
        // Add others if exists in data
        if (data.others && data.others.percentage > 0) {
          items.push({
            name: 'Others',
            value: data.others.percentage,
            displayValue: data.others.job_count || 'Others',
            displayUnit: data.others.job_count ? 'jobs' : '',
            displayName: 'Others'
          });
        }
        break;

      default:
        break;
    }

    // Apply 1% rule: merge items with less than 1% into others
    const mainItems = items.filter(item => item.value >= 1 || item.name === 'Others');
    const smallItems = items.filter(item => item.value < 1 && item.name !== 'Others');
    
    // If there are small items, merge them with existing Others or create new Others
    if (smallItems.length > 0) {
      const smallItemsSum = smallItems.reduce((sum, item) => sum + item.value, 0);
      const existingOthers = mainItems.find(item => item.name === 'Others');
      
      if (existingOthers) {
        // Merge with existing Others
        existingOthers.value += smallItemsSum;
      } else {
        // Create new Others
        mainItems.push({
          name: 'Others',
          value: smallItemsSum,
          displayValue: 'Others',
          displayUnit: '',
          displayName: 'Others'
        });
      }
    }

    // Sort by value descending, but keep Others at the end
    const sortedItems = mainItems
      .filter(item => item.name !== 'Others')
      .sort((a, b) => b.value - a.value);
    
    const othersItem = mainItems.find(item => item.name === 'Others');
    if (othersItem) {
      sortedItems.push(othersItem);
    }

    return sortedItems;
  }, [data, type]);

  // Check if data is empty or all values are zero
  const hasNoData = processedData.length === 0 ||
    processedData.every(item => item.value === 0);

  // Determine no-data message based on title
  const getNoDataMessage = () => {
    if (title === 'Top Agent' || title === 'Top Region') {
      return (
        <>
          <div>No data available</div>
          <div>Add jobs or complete quote info to see results</div>
        </>
      );
    }
    return (
      <>
        <div>No data available</div>
        <div>Add jobs to see results</div>
      </>
    );
  };

  const activeData = processedData[activeIndex] || processedData[0];

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(0); // Return to first item
  };

  return (
    <Card className="pie-chart-card">
      <Card.Body className="pie-chart-body">
        <h3 className="pie-chart-title">{title}</h3>
        <div className="pie-chart-content">
          {hasNoData ? (
            <div className="pie-chart-no-data">
              {getNoDataMessage()}
            </div>
          ) : (
            <>
              <div className="pie-chart-container">
                <ResponsiveContainer width="100%" height={200}>
                  <RechartsPieChart>
                    <Pie
                      data={processedData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      onMouseEnter={onPieEnter}
                      onMouseLeave={onPieLeave}
                    >
                      {processedData.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                          className={index === activeIndex ? 'active-sector' : ''}
                        />
                      ))}
                    </Pie>
                  </RechartsPieChart>
                </ResponsiveContainer>
                <div className="pie-chart-center">
                  <span className="pie-chart-percentage">{activeData?.value?.toFixed(1)}%</span>
                </div>
              </div>
              <div className="pie-chart-info">
                <div className="primary-value">
                  <span className="value-number">{activeData?.displayValue || 'N/A'}</span>
                  {activeData?.displayUnit && <span className="value-unit"> {activeData.displayUnit}</span>}
                </div>
                <div className="primary-label">{activeData?.displayName || 'N/A'}</div>
              </div>
            </>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

PieChart.propTypes = {
  title: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['region', 'agent', 'quote', 'property']).isRequired,
  data: PropTypes.object.isRequired,
};

export default PieChart;